import { getColumnSortValue } from "./columns.js";

/**
 * Core pure functions for sorting, formatting, and utilities
 * These functions have no side effects and are fully testable
 *
 * Updated for unified data format where item.ranks = { context: [rank, change], ... }
 */

// Debug logging - silent unless ?debug=1 or debug file loaded
export function log(...args) {
  if (typeof window === "undefined") return;
  const debug = window.__AXIOM_DEBUG__ || new URLSearchParams(window.location.search).has("debug");
  if (!debug) return;
  console.log(...args);
  if (window.axiomLog) window.axiomLog("debug", args.join(" "));
}

// Sort cache - maps cacheKey -> sorted indices array (LRU with max 500 entries)
const sortCache = new Map();
const SORT_CACHE_MAX_SIZE = 500;
const sortCacheStats = {
  hits: 0,
  misses: 0,
  evictions: 0,
};
const CREATOR_DELTA_SORT_COLUMNS = new Set([
  "templatesdelta",
  "componentsdelta",
  "pluginsdelta",
  "vectorsdelta",
  "tutorialsdelta",
  "totaldelta",
]);

// Expose cache for debugging
if (typeof window !== "undefined") {
  window.RanksSortCache = sortCache;
}

/**
 * Clear the sort cache (useful when data changes)
 */
export function clearSortCache() {
  sortCache.clear();
  sortCacheStats.hits = 0;
  sortCacheStats.misses = 0;
  sortCacheStats.evictions = 0;
}

/**
 * Read sort cache metrics (for perf tests/benchmarks)
 * @returns {{hits:number, misses:number, evictions:number, size:number}}
 */
export function getSortCacheStats() {
  return {
    hits: sortCacheStats.hits,
    misses: sortCacheStats.misses,
    evictions: sortCacheStats.evictions,
    size: sortCache.size,
  };
}

/**
 * Get the rank value for an item in a given context
 * @param {Object} item - The unified item
 * @param {string} context - The context (all, free, paid, or categoryId)
 * @returns {number|null} The rank value
 */
export function getRank(item, context) {
  const entry = item.ranks?.[context];
  return entry ? entry[0] : null;
}

/**
 * Get the change value for an item in a given context
 * @param {Object} item - The unified item
 * @param {string} context - The context (all, free, paid, or categoryId)
 * @returns {number|string|null} The change value (number, "new", "moved-in", "comeback", "price_change", or null)
 */
export function getChange(item, context) {
  const entry = item.ranks?.[context];
  return entry ? entry[1] : null;
}

/**
 * Get a sortable medal score for an item.
 * Uses precomputed value from static JSON generation.
 * @param {Object} item - The data item
 * @returns {number} Medal score
 */
export function getMedalSortScore(item) {
  if (item?.type === "creator") return 0;
  const score = Number(item?.badges?.medalScore);
  return Number.isFinite(score) && score > 0 ? score : 0;
}

/**
 * Get the sort value for an item based on column and context
 * @param {Object} item - The data item
 * @param {string} column - The column to sort by (rank, change, price, name, medals, author)
 * @param {string} context - The context (all, free, paid, or categoryId)
 * @returns {*} The sort value
 */
export function getSortValue(item, column, context = "all") {
  return getColumnSortValue(item, column, context);
}

/**
 * Compute sorted indices for items
 * @param {Array} items - Array of unified items
 * @param {string} column - Column to sort by (rank, change, price, name, medals, author)
 * @param {string} direction - 'asc' or 'desc'
 * @param {string} context - The context (all, free, paid, or categoryId)
 * @returns {Array<number>} Array of sorted indices
 */
export function computeSortedIndices(items, column, direction, context = "all") {
  log(`[SORT] Computing indices: column=${column}, direction=${direction}, context=${context}, items=${items.length}`);
  const startTime = performance.now();

  const indices = items
    .map((_, index) => index)
    .sort((a, b) => {
      const valueA = getSortValue(items[a], column, context);
      const valueB = getSortValue(items[b], column, context);
      const isCreatorDeltaSort = items[a]?.type === "creator" && CREATOR_DELTA_SORT_COLUMNS.has(column);
      const typeA = items[a]?.type || "template";
      const typeB = items[b]?.type || "template";
      const isNonTemplateCategorySort = column === "categories" && typeA !== "template" && typeB !== "template";

      // Handle null/undefined values — always sort to end regardless of direction
      if (valueA === null || valueA === undefined) {
        if (valueB === null || valueB === undefined) return 0;
        if (isCreatorDeltaSort) return 1;
        return 1;
      }
      if (valueB === null || valueB === undefined) {
        if (isCreatorDeltaSort) return -1;
        return -1;
      }

      if (isNonTemplateCategorySort) {
        const emptyA = typeof valueA === "string" && valueA.trim() === "";
        const emptyB = typeof valueB === "string" && valueB.trim() === "";
        if (emptyA !== emptyB) {
          return emptyA ? (direction === "desc" ? -1 : 1) : direction === "desc" ? 1 : -1;
        }
      }

      // Compare values
      if (valueA < valueB) return direction === "asc" ? -1 : 1;
      if (valueA > valueB) return direction === "asc" ? 1 : -1;
      return 0;
    });

  const duration = Math.round(performance.now() - startTime);
  log(`[SORT] Computed ${indices.length} indices in ${duration}ms`);

  return indices;
}

/**
 * Build a cache key from filter state
 * @param {Object} filterState - Object with type, category, tab, column, direction, search
 * @returns {string} Cache key
 */
export function buildCacheKey(filterState) {
  const { type, category, tab, column, direction, search } = filterState;

  // Normalize search: trim, lowercase, remove extra whitespace
  const normalizedSearch = search ? search.trim().toLowerCase().replace(/\s+/g, "") : "";

  // Build key parts
  const parts = [type || "all"];

  if (category) {
    parts.push(`cat${category}`);
  }

  parts.push(tab || "all");
  parts.push(column || "rank");
  parts.push(direction || "asc");

  if (normalizedSearch) {
    parts.push(normalizedSearch);
  }

  return parts.join("-");
}

/**
 * Get or compute sorted indices with caching
 * @param {Array} items - Array of unified items
 * @param {string} column - Column to sort by
 * @param {string} direction - 'asc' or 'desc'
 * @param {string} context - The context (all, free, paid, or categoryId)
 * @param {string} cacheKey - Pre-built cache key from filter state
 * @returns {Array<number>} Array of sorted indices
 */
export function getSortedIndices(items, column, direction, context = "all", cacheKey = null) {
  // Use provided cache key or build a simple one
  const key = cacheKey || `${context}-${column}-${direction}`;

  if (sortCache.has(key)) {
    sortCacheStats.hits += 1;
    log(`[SORT] Cache HIT: ${key} (${sortCache.size}/${SORT_CACHE_MAX_SIZE})`);
    // LRU: move to end by deleting and re-adding
    const value = sortCache.get(key);
    sortCache.delete(key);
    sortCache.set(key, value);
    return value;
  }

  log(`[SORT] Cache MISS: ${key} (${sortCache.size}/${SORT_CACHE_MAX_SIZE})`);
  sortCacheStats.misses += 1;
  const indices = computeSortedIndices(items, column, direction, context);

  // LRU eviction: remove oldest entries if at capacity
  while (sortCache.size >= SORT_CACHE_MAX_SIZE) {
    const oldestKey = sortCache.keys().next().value;
    sortCache.delete(oldestKey);
    sortCacheStats.evictions += 1;
    log(`[SORT] Cache EVICT: ${oldestKey}`);
  }

  sortCache.set(key, indices);
  return indices;
}

/**
 * Helper to escape HTML entities
 * @param {*} text - The text to escape
 * @returns {string} The escaped text
 */
export function escapeHtml(text) {
  if (text === undefined || text === null) return "";
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Normalize a search value for comparison
 * @param {*} value - The value to normalize
 * @returns {string} The normalized value
 */
export function normalizeSearchValue(value) {
  return String(value ?? "").toLowerCase();
}

function getCountrySearchValues(value) {
  const code = String(value ?? "")
    .trim()
    .toUpperCase();
  if (!code) return [];
  let displayName = "";
  try {
    displayName = new Intl.DisplayNames(["en"], { type: "region" }).of(code) || "";
  } catch {
    // Keep country-code search working in runtimes without Intl region names.
  }
  return [code, displayName].map(normalizeSearchValue);
}

/**
 * Check if an item matches a search query
 * @param {Object} item - The unified item
 * @param {string} query - The search query (lowercase)
 * @param {Object} enabledFields - Which fields to search
 * @returns {boolean}
 */
export function matchesSearch(item, query, enabledFields = {}) {
  if (!query) return true;

  if (item?.type === "creator") {
    const fields = [
      item.name,
      item.creatorSlug,
      item.creatorStats?.totalItems,
      item.creatorStats?.totalTemplates,
      item.creatorStats?.totalComponents,
    ];
    return fields.some((value) => normalizeSearchValue(value).includes(query));
  }

  const {
    searchRank = true,
    searchChange = true,
    searchPrice = true,
    searchName = true,
    searchAuthor = true,
    searchCountry = true,
  } = enabledFields;

  // Name search
  if (searchName && normalizeSearchValue(item.name).includes(query)) {
    return true;
  }

  // Author search
  if (searchAuthor && normalizeSearchValue(item.authorName).includes(query)) {
    return true;
  }

  // Country search (used by Experts)
  if (searchCountry && getCountrySearchValues(item.country).some((value) => value.includes(query))) {
    return true;
  }

  // Price search
  if (searchPrice && normalizeSearchValue(item.price).includes(query)) {
    return true;
  }

  // Rank search (check all contexts)
  if (searchRank) {
    for (const [_context, entry] of Object.entries(item.ranks || {})) {
      const rank = entry[0];
      if (rank !== null && String(rank).includes(query)) {
        return true;
      }
    }
  }

  // Change search (check all contexts)
  if (searchChange) {
    for (const [_context, entry] of Object.entries(item.ranks || {})) {
      const change = entry[1];
      if (change !== null) {
        const changeStr = typeof change === "number" ? (change > 0 ? `+${change}` : String(change)) : String(change);
        if (changeStr.toLowerCase().includes(query)) {
          return true;
        }
      }
    }
  }

  return false;
}
